/**
 * OKDoc tracking workspace: список shortlink-ов, агрегаты и timeline событий.
 * Дата: 17.03.2026
 */
import { useEffect, useMemo, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { FlowNote } from '@/features/monitoring/FlowNote';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DateRangePicker,
  EmptyState,
  FilterSelect,
  LoadingState,
  Modal,
  Pagination,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TenantSelector,
} from '@/components/ui';
import { okdocTrackingApi } from '@/api/okdocTracking';
import { RecommendationsScenarioPreview } from '@/features/okdoc/admin/recommendations/RecommendationsScenarioPreview';
import {
  OKDOC_CTA_ACTION_OPTIONS,
  OKDOC_INTERACTION_INTENT_OPTIONS,
  getOkdocCTAActionLabel,
  getOkdocInteractionIntentLabel,
} from '@/features/okdoc/registry/ctaRegistry';
import {
  OKDOC_SCENARIO_FAMILY_OPTIONS,
  OKDOC_SCENARIO_KIND_OPTIONS,
  OKDOC_SOURCE_CONTEXT_OPTIONS,
  getOkdocScenarioFamilyLabel,
  getOkdocScenarioKindLabel,
  getOkdocSourceContextLabel,
} from '@/features/okdoc/registry/scenarioRegistry';
import { useTenantScope } from '@/hooks/useTenantScope';
import type {
  OkdocFlow,
  OkdocLinkEventType,
  OkdocTrackingEvent,
  OkdocTrackingDetailsResponse,
  OkdocTrackingLink,
  OkdocTrackingSummary,
} from '../../../types/okdocTracking';
import { OKDOC_EVENT_LABELS, OKDOC_FLOW_LABELS } from '../../../types/okdocTracking';
import { formatDateTime, formatPhone } from '@/utils/formatters';
import { endOfDay, format, startOfDay, subDays } from 'date-fns';

const PAGE_SIZE = 25;

const flowOptions = Object.entries(OKDOC_FLOW_LABELS).map(([value, label]) => ({ value, label: String(label) }));
const eventOptions = Object.entries(OKDOC_EVENT_LABELS).map(([value, label]) => ({ value, label: String(label) }));
const scenarioFamilyOptions = OKDOC_SCENARIO_FAMILY_OPTIONS;
const scenarioKindOptions = OKDOC_SCENARIO_KIND_OPTIONS;
const sourceContextOptions = OKDOC_SOURCE_CONTEXT_OPTIONS;
const ctaActionOptions = OKDOC_CTA_ACTION_OPTIONS;
const interactionIntentOptions = OKDOC_INTERACTION_INTENT_OPTIONS;

// 19.03.26 2.: подбирает цвет badge по типу OKDoc события.
function getEventBadgeVariant(eventType?: OkdocLinkEventType): 'success' | 'warning' | 'error' | 'info' | 'secondary' {
  switch (eventType) {
    case 'submit_success':
    case 'review_click':
      return 'success';
    case 'cta_click':
      return 'warning';
    case 'submit_attempt':
    case 'landing_open':
    case 'landing_reopen':
      return 'info';
    case 'redirect_click':
      return 'warning';
    case 'submit_rejected_used':
    case 'submit_rejected_expired':
      return 'error';
    default:
      return 'secondary';
  }
}

function readEventMetaString(event: OkdocTrackingEvent, key: string): string {
  const value = event.metadata?.[key];
  return typeof value === 'string' ? value : '';
}

// 19.03.26 2.: строит ссылку на public landing для админского preview.
function buildPublicOkdocUrl(code?: string): string {
  if (!code) {
    return '/okdoc';
  }
  if (typeof window === 'undefined') {
    return `/okdoc/${code}`;
  }
  return `${window.location.origin}/okdoc/${code}`;
}

// 19.03.26 2.: admin workspace для OKDoc tracking и scenario preview.
// Связанный док: docs/living/okdoc/OKDOC_SCENARIOS_FOUNDATION.md
// При изменении tracking/admin preview обновляйте и код, и док.
export function OkdocTrackingPageContent() {
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant } = useTenantScope();

  const [loading, setLoading] = useState(false);
  const [links, setLinks] = useState<OkdocTrackingLink[]>([]);
  const [summary, setSummary] = useState<OkdocTrackingSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [flow, setFlow] = useState<OkdocFlow | ''>('');
  const [eventType, setEventType] = useState<OkdocLinkEventType | ''>('');
  const [scenarioFamily, setScenarioFamily] = useState('');
  const [scenarioKind, setScenarioKind] = useState('');
  const [sourceContext, setSourceContext] = useState('');
  const [ctaActionKind, setCtaActionKind] = useState('');
  const [interactionIntent, setInteractionIntent] = useState('');
  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>({
    from: startOfDay(subDays(new Date(), 7)),
    to: endOfDay(new Date()),
  });

  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);
  const [details, setDetails] = useState<OkdocTrackingDetailsResponse | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    setOffset(0);
  }, [search, flow, eventType, scenarioFamily, scenarioKind, sourceContext, ctaActionKind, interactionIntent, dateRange.from, dateRange.to, tenantId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await okdocTrackingApi.list({
          tenant_id: tenantId || undefined,
          search: search || undefined,
          flow: flow || undefined,
          event_type: eventType || undefined,
          scenario_family: scenarioFamily || undefined,
          scenario_kind: scenarioKind || undefined,
          source_context: sourceContext || undefined,
          cta_action_kind: ctaActionKind || undefined,
          interaction_intent: interactionIntent || undefined,
          date_from: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
          date_to: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
          limit: PAGE_SIZE,
          offset,
        });
        if (cancelled) {
          return;
        }
        setLinks(response.links || []);
        setSummary(response.summary || null);
        setTotal(response.total || 0);
      } catch {
        if (!cancelled) {
          setLinks([]);
          setSummary(null);
          setTotal(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, search, flow, eventType, scenarioFamily, scenarioKind, sourceContext, ctaActionKind, interactionIntent, dateRange.from, dateRange.to, offset]);

  useEffect(() => {
    if (!selectedLinkId) {
      setDetails(null);
      return;
    }

    let cancelled = false;
    const loadDetails = async () => {
      setDetailsLoading(true);
      try {
        const response = await okdocTrackingApi.getById(selectedLinkId, tenantId);
        if (!cancelled) {
          setDetails(response);
        }
      } catch {
        if (!cancelled) {
          setDetails(null);
        }
      } finally {
        if (!cancelled) {
          setDetailsLoading(false);
        }
      }
    };

    loadDetails();
    return () => {
      cancelled = true;
    };
  }, [selectedLinkId, tenantId]);

  const conversionPercent = useMemo(() => {
    if (!summary) {
      return '0%';
    }
    return `${Math.round((summary.conversion_rate || 0) * 100)}%`;
  }, [summary]);

  return (
    <MainLayout headerTitle="OKDoc переходы" disableMainScroll>
      <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 space-y-2 border-b border-shell-border bg-shell-surface px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          {isSuperAdmin && (
            <div className="w-full max-w-xs">
              <TenantSelector
                value={selectedTenantId || ''}
                onChange={setSelectedTenant}
                showLabel={false}
              />
            </div>
          )}
          <div className="min-w-[220px] flex-1">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Поиск по ФИО, телефону или коду..."
            />
          </div>
          <div className="w-44">
            <FilterSelect
              value={scenarioKind}
              onChange={setScenarioKind}
              options={scenarioKindOptions}
              allOptionLabel="Все сценарии"
              placeholder="Все сценарии"
            />
          </div>
          <button type="button" className="shell-button-secondary h-9" onClick={() => setFiltersOpen((open) => !open)}>
            Фильтры
          </button>
          <button type="button" className="shell-button-secondary h-9" onClick={() => setShowGuide((open) => !open)}>
            Справка
          </button>
        </div>
        <DateRangePicker
          from={dateRange.from}
          to={dateRange.to}
          onChange={(from, to) => setDateRange({ from, to })}
        />
        {filtersOpen && (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3 xl:grid-cols-6">
            <FilterSelect value={flow} onChange={(value) => setFlow(value as OkdocFlow | '')} options={flowOptions} allOptionLabel="Все flow" placeholder="Все flow" />
            <FilterSelect value={eventType} onChange={(value) => setEventType(value as OkdocLinkEventType | '')} options={eventOptions} allOptionLabel="Все события" placeholder="Все события" />
            <FilterSelect value={scenarioFamily} onChange={setScenarioFamily} options={scenarioFamilyOptions} allOptionLabel="Все family" placeholder="Все family" />
            <FilterSelect value={sourceContext} onChange={setSourceContext} options={sourceContextOptions} allOptionLabel="Все источники" placeholder="Все источники" />
            <FilterSelect value={ctaActionKind} onChange={setCtaActionKind} options={ctaActionOptions} allOptionLabel="Все CTA" placeholder="Все CTA" />
            <FilterSelect value={interactionIntent} onChange={setInteractionIntent} options={interactionIntentOptions} allOptionLabel="Все intent" placeholder="Все intent" />
          </div>
        )}
        {isSuperAdmin && !tenantId && (
          <p className="text-xs leading-4 text-shell-muted">Показаны все клиники. Выберите клинику, чтобы сузить сводку и таблицу.</p>
        )}
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {showGuide && <FlowNote id="okdocTracking" />}
        <div className="rounded-xl border border-shell-border bg-shell-surface px-4 py-3">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <FunnelStep label="Ссылки" value={summary?.total_links ?? 0} hint="записи выборки" />
            <FunnelStep label="Открытия" value={summary?.landing_opens ?? 0} hint={`${summary?.opened_links ?? 0} ссылок открывали`} />
            <FunnelStep label="CTA" value={summary?.cta_clicks ?? 0} hint="события клика" />
            <FunnelStep label="Ответы" value={summary?.submit_success ?? 0} hint="успешные submit" />
            <FunnelStep label="Конверсия" value={conversionPercent} hint="ответы / открытые ссылки" />
          </div>
          <p className="mt-2 text-xs leading-4 text-shell-muted">
            Ссылки — записи, у которых создание или последнее событие попадает в период. Открытия, CTA и ответы — число событий за период, не уникальные пациенты. Конверсия делит успешные ответы на ссылки выборки, которые открывали хотя бы раз.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              OKDoc ссылки ({total}){isSuperAdmin && !tenantId ? ' · все клиники' : ''}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <LoadingState />
            ) : links.length === 0 ? (
              <EmptyState
                type="search"
                title="Данных пока нет"
                description="По текущим фильтрам OKDoc события не найдены."
              />
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      {isSuperAdmin && <TableHeader>Клиника</TableHeader>}
                      <TableHeader>Код</TableHeader>
                      <TableHeader>Пациент</TableHeader>
                      <TableHeader>Сценарий</TableHeader>
                      <TableHeader>Открытия</TableHeader>
                      <TableHeader>Submit</TableHeader>
                      <TableHeader>Последнее событие</TableHeader>
                      <TableHeader>Создана</TableHeader>
                      <TableHeader>Детали</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {links.map((link) => (
                      <TableRow key={link.id}>
                        {isSuperAdmin && (
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium">{link.tenant_name || '—'}</div>
                              <div className="text-xs text-neutral-500">{link.tenant_slug || link.tenant_id}</div>
                            </div>
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">{link.code}</div>
                            {link.used_action && (
                              <Badge variant="secondary" className="text-xs">
                                {link.used_action}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div>{link.patient_name || '—'}</div>
                            <div className="text-xs text-neutral-500">{formatPhone(link.phone)}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <Badge variant="info">{OKDOC_FLOW_LABELS[link.flow] || link.flow}</Badge>
                            {link.scenario_kind && (
                              <Badge variant="secondary" className="text-xs">
                                {getOkdocScenarioKindLabel(link.scenario_kind)}
                              </Badge>
                            )}
                            {link.scenario_family && (
                              <Badge variant="secondary" className="text-xs">
                                {getOkdocScenarioFamilyLabel(link.scenario_family)}
                              </Badge>
                            )}
                            {link.source_context && (
                              <Badge variant="secondary" className="text-xs">
                                {getOkdocSourceContextLabel(link.source_context)}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">{link.open_count}</div>
                            <div className="text-xs text-neutral-500">{formatDateTime(link.last_opened_at)}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">{link.submit_count}</div>
                            <div className="text-xs text-neutral-500">{formatDateTime(link.first_submitted_at)}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {link.last_event_type ? (
                              <Badge variant={getEventBadgeVariant(link.last_event_type)}>
                                {OKDOC_EVENT_LABELS[link.last_event_type] || link.last_event_type}
                              </Badge>
                            ) : (
                              <span className="text-sm text-neutral-400">—</span>
                            )}
                            <div className="text-xs text-neutral-500">{formatDateTime(link.last_event_at)}</div>
                          </div>
                        </TableCell>
                        <TableCell>{formatDateTime(link.created_at)}</TableCell>
                        <TableCell>
                          <button
                            type="button"
                            className="text-sm font-medium text-primary-600 hover:text-primary-700"
                            onClick={() => setSelectedLinkId(link.id)}
                          >
                            Открыть
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="mt-4">
                  <Pagination
                    offset={offset}
                    limit={PAGE_SIZE}
                    total={total}
                    onOffsetChange={setOffset}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Modal
          isOpen={Boolean(selectedLinkId)}
          onClose={() => setSelectedLinkId(null)}
          title={details?.link?.code ? `OKDoc: ${details.link.code}` : 'OKDoc ссылка'}
          size="xl"
        >
          {detailsLoading || !details ? (
            <LoadingState />
          ) : (
            <div className="space-y-6">
              <div className={`grid gap-4 ${details.link.scenario_profile ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)]' : 'md:grid-cols-2'}`}>
                <Card>
                  <CardContent className="space-y-2 pt-6">
                    <div className="text-sm text-neutral-500">Клиника</div>
                    <div className="font-medium">{details.link.tenant_name || '—'}</div>
                    <div className="text-sm text-neutral-500">{details.link.tenant_slug || details.link.tenant_id}</div>
                    <div className="border-t border-neutral-100 pt-2" />
                    <div className="text-sm text-neutral-500">Пациент</div>
                    <div className="font-medium">{details.link.patient_name || '—'}</div>
                    <div className="text-sm text-neutral-500">{formatPhone(details.link.phone)}</div>
                    <div className="border-t border-neutral-100 pt-2" />
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Badge variant="info">{OKDOC_FLOW_LABELS[details.link.flow] || details.link.flow}</Badge>
                      {details.link.scenario_kind && (
                        <Badge variant="secondary">{getOkdocScenarioKindLabel(details.link.scenario_kind)}</Badge>
                      )}
                      {details.link.scenario_family && (
                        <Badge variant="secondary">{getOkdocScenarioFamilyLabel(details.link.scenario_family)}</Badge>
                      )}
                      {details.link.source_context && (
                        <Badge variant="secondary">{getOkdocSourceContextLabel(details.link.source_context)}</Badge>
                      )}
                    </div>
                    <div className="pt-3">
                      <a
                        href={buildPublicOkdocUrl(details.link.code)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm font-medium text-primary-600 hover:text-primary-700"
                      >
                        Открыть public landing
                      </a>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="space-y-2 pt-6">
                    <div className="text-sm text-neutral-500">Агрегаты</div>
                    <div>
                      Открытия: <span className="font-medium">{details.link.open_count}</span>
                    </div>
                    <div>
                      CTA клики: <span className="font-medium">{details.events.filter((event: OkdocTrackingEvent) => event.event_type === 'cta_click').length}</span>
                    </div>
                    <div>
                      Submit: <span className="font-medium">{details.link.submit_count}</span>
                    </div>
                    <div>
                      Последнее событие:{' '}
                      <span className="font-medium">
                        {details.link.last_event_type ? OKDOC_EVENT_LABELS[details.link.last_event_type] || details.link.last_event_type : '—'}
                      </span>
                    </div>
                    <div>
                      Последний IP: <span className="font-medium">{details.link.last_ip || '—'}</span>
                    </div>
                    <div>
                      Источник: <span className="font-medium">{details.link.source_channel || '—'}</span>
                    </div>
                  </CardContent>
                </Card>
                {details.link.scenario_profile && (
                  <RecommendationsScenarioPreview profile={details.link.scenario_profile} />
                )}
              </div>

              <div className="space-y-3">
                <h3 className="text-lg font-semibold">Лента событий</h3>
                {details.events.length === 0 ? (
                  <EmptyState
                    type="search"
                    title="Событий пока нет"
                    description="Для этой ссылки ещё не зафиксированы OKDoc события."
                  />
                ) : (
                  <div className="space-y-3">
                    {details.events.map((event: OkdocTrackingEvent) => (
                      <div key={event.id} className="space-y-2 rounded-xl border border-neutral-200 p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant={getEventBadgeVariant(event.event_type)}>
                            {OKDOC_EVENT_LABELS[event.event_type] || event.event_type}
                          </Badge>
                          <span className="text-sm text-neutral-500">{formatDateTime(event.created_at)}</span>
                        </div>
                        <div className="text-sm text-neutral-700">
                          IP: {event.ip_address || '—'} | Referer: {event.referer || '—'}
                        </div>
                        {(readEventMetaString(event, 'cta_action_kind') || readEventMetaString(event, 'interaction_intent')) && (
                          <div className="flex flex-wrap gap-2">
                            {readEventMetaString(event, 'cta_action_kind') && (
                              <Badge variant="secondary">{getOkdocCTAActionLabel(readEventMetaString(event, 'cta_action_kind'))}</Badge>
                            )}
                            {readEventMetaString(event, 'interaction_intent') && (
                              <Badge variant="secondary">{getOkdocInteractionIntentLabel(readEventMetaString(event, 'interaction_intent'))}</Badge>
                            )}
                          </div>
                        )}
                        {event.metadata && Object.keys(event.metadata).length > 0 && (
                          <pre className="overflow-x-auto rounded-lg bg-neutral-50 p-3 text-xs text-neutral-700">
                            {JSON.stringify(event.metadata, null, 2)}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal>
      </div>
      </div>
    </MainLayout>
  );
}

function FunnelStep({ label, value, hint }: { label: string; value: string | number; hint: string }) {
  return (
    <div>
      <p className="text-xs leading-4 text-shell-muted">{label}</p>
      <p className="text-2xl font-semibold leading-7 text-shell-strong">{value}</p>
      <p className="text-xs leading-4 text-shell-muted">{hint}</p>
    </div>
  );
}
